<template>
  <!-- The owner's own details, the first thing on Suite Home until they are given
       (issue 812). The setup wizard asks the same thing as its first step; an
       owner who skips the wizard or starts empty never meets it, so this is where
       it finds them. It IS the wizard's section, so the two can never disagree about
       what is asked or what saving does (it confirms the record in People). It
       stays out of sight while the record is confirmed, unreadable or still
       loading. "Later" is a deliberate skip: it asks first what staying
       unconfirmed means, and is remembered for the day only. -->
  <section
    v-show="state === 'unconfirmed'"
    ref="root"
    class="rounded-2xl border border-accent-ring bg-surface p-6 shadow-md ring-2 ring-accent-ring"
    data-testid="own-details-prompt"
    role="region"
    :aria-label="t('onboarding.wizard.details.title')"
  >
    <WizardDetailsSection ref="section" @state="state = $event" />
    <div v-if="state === 'unconfirmed'" class="mt-4 flex justify-end gap-2">
      <Button variant="ghost" :disabled="saving" data-testid="own-details-later" @click="later">
        {{ t('homebase.ownDetails.later') }}
      </Button>
      <Button :loading="saving" data-testid="own-details-save" @click="save">
        {{ t('homebase.ownDetails.save') }}
      </Button>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { Button } from '@lumi/ui';
import { useToast } from '@lumi/app-kit';
import WizardDetailsSection from '@/components/onboarding/WizardDetailsSection.vue';
import { useOwnDetailsSkipWarning } from '@/composables/useOwnDetailsSkipWarning';
import { useAuthStore } from '@/stores/auth';
import { useTenantsStore } from '@/stores/tenants';
import { skipOwnDetailsPromptToday } from '@/utils/ownDetailsPrompt';

const emit = defineEmits<{ (e: 'dismissed'): void }>();
const { t } = useI18n();
const toast = useToast();
const authStore = useAuthStore();
const tenantsStore = useTenantsStore();
const { confirmSkip } = useOwnDetailsSkipWarning();

type State = 'loading' | 'unconfirmed' | 'confirmed' | 'unavailable';
const state = ref<State>('loading');
const root = ref<HTMLElement | null>(null);
const section = ref<{ persistComplete?: () => Promise<boolean> } | null>(null);
const saving = ref(false);

async function save() {
  saving.value = true;
  try {
    // The section vetoes (names missing, People refused) in its own words and
    // stays unconfirmed; only a confirmed record closes the prompt.
    if (await section.value?.persistComplete?.()) toast.success(t('homebase.ownDetails.saved'));
  } finally {
    saving.value = false;
  }
}

// Skipping is allowed, never silent: the warning says what an unconfirmed record
// means, and only an explicit "skip" puts the prompt away (for today).
async function later() {
  if (!(await confirmSkip())) {
    const first = root.value?.querySelector<HTMLElement>('[data-test="wizard-details-first-name"]');
    const control = first?.matches('input') ? first : first?.querySelector('input');
    control?.focus();
    return;
  }
  skipOwnDetailsPromptToday(authStore.user?.userId, tenantsStore.activeTenantId);
  emit('dismissed');
}
</script>
